import {
  BoxGeometry,
  BufferGeometry,
  CircleGeometry,
  CylinderGeometry,
  Float32BufferAttribute,
  Group,
  InstancedMesh,
  type Material,
  Matrix4,
} from "three";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";

import { ROAD_SIGN_RENDER_CONFIG } from "../../config/roadSigns.ts";
import { pointInPolygon } from "../../world/geometry.ts";
import type {
  RuntimeLinearFeature,
  RuntimeRoadControl,
  RuntimeRoadControlKind,
  RuntimeWorldTilePayloadV2,
} from "../../world/schema/runtimeTile.ts";
import type { TerrainTileData } from "../../world/schema/tile.ts";
import {
  streetFeatureRoadEdgeWidthM,
  streetFeatureSurfaceElevationM,
} from "../../world/streets.ts";
import type { CityMaterialLibrary } from "../materials/cityMaterials.ts";

type RegulatoryRoadSignKind = Exclude<RuntimeRoadControlKind, "crossing">;

export interface RegulatoryRoadSignPlacement {
  readonly control: Readonly<RuntimeRoadControl>;
  readonly kind: RegulatoryRoadSignKind;
  readonly roadId: string;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly yaw: number;
  readonly fallbackStep: number;
}

export interface RegulatoryRoadSignBatch {
  readonly object: Group;
  readonly signCount: number;
  readonly skippedControlCount: number;
}

interface SignPart {
  readonly name: string;
  readonly geometry: BufferGeometry;
  readonly material: Material;
}

const SIGN_GROUND_LIFT_M = 0.012;

export function regulatoryRoadSignPlacementsForTile(
  tile: Readonly<RuntimeWorldTilePayloadV2>,
  terrain: Readonly<TerrainTileData> = tile.terrain,
): readonly Readonly<RegulatoryRoadSignPlacement>[] {
  const roads = tile.transportLines.filter((feature) => feature.kind === "road");
  const placements = (tile.roadControls ?? []).flatMap((control) => {
    if (control.kind === "crossing") return [];
    if (control.kind === "speed_limit" && control.value === undefined) return [];
    const road = roads.find((candidate) =>
      sourceFeatureId(candidate) === control.roadFeatureId
    );
    if (road === undefined) return [];
    const travel = travelDirection(control, road);
    const front = { x: -travel.x, z: -travel.z };
    if (control.placement === "exact-traffic-sign-node") {
      const surfaceY = streetFeatureSurfaceElevationM(
        road,
        terrain,
        tile.origin,
        control.roadPosition,
      );
      if (surfaceY === null) return [];
      const placement: RegulatoryRoadSignPlacement = {
        control,
        kind: control.kind,
        roadId: road.id,
        x: control.position.x,
        y: surfaceY + SIGN_GROUND_LIFT_M,
        z: control.position.z,
        yaw: control.directionDegrees === undefined
          ? Math.atan2(front.x, front.z)
          : Math.PI + control.directionDegrees * Math.PI / 180,
        fallbackStep: 0,
      };
      return placementIsClear(placement, tile) ? [placement] : [];
    }
    const right = { x: -travel.z, z: travel.x };
    const roadOffsetM = streetFeatureRoadEdgeWidthM(road) / 2 +
      ROAD_SIGN_RENDER_CONFIG.regulatoryRoadsideClearanceM;
    for (
      let fallbackStep = 0;
      fallbackStep < ROAD_SIGN_RENDER_CONFIG.regulatoryFallbackAttemptCount;
      fallbackStep += 1
    ) {
      const approachOffsetM = ROAD_SIGN_RENDER_CONFIG.regulatoryApproachOffsetM +
        fallbackStep * ROAD_SIGN_RENDER_CONFIG.regulatoryFallbackAlongStepM;
      const point = {
        x: control.roadPosition.x + right.x * roadOffsetM -
          travel.x * approachOffsetM,
        z: control.roadPosition.z + right.z * roadOffsetM -
          travel.z * approachOffsetM,
      };
      const surfaceY = streetFeatureSurfaceElevationM(
        road,
        terrain,
        tile.origin,
        point,
      );
      if (surfaceY === null) continue;
      const placement: RegulatoryRoadSignPlacement = {
        control,
        kind: control.kind,
        roadId: road.id,
        ...point,
        y: surfaceY + SIGN_GROUND_LIFT_M,
        yaw: Math.atan2(front.x, front.z),
        fallbackStep,
      };
      if (placementIsClear(placement, tile)) return [placement];
    }
    return [];
  });
  const unique = new Map<string, RegulatoryRoadSignPlacement>();
  for (const placement of placements) {
    const key = [
      placement.kind,
      placement.control.value ?? "",
      placement.control.supplementaryCodes?.join("+") ?? "",
      Math.round(placement.x * 100),
      Math.round(placement.z * 100),
      Math.round(placement.yaw * 1_000),
    ].join(":");
    if (!unique.has(key)) unique.set(key, placement);
  }
  return [...unique.values()];
}

export function createRegulatoryRoadSignBatch(
  tile: Readonly<RuntimeWorldTilePayloadV2>,
  terrain: Readonly<TerrainTileData>,
  materials: CityMaterialLibrary,
): RegulatoryRoadSignBatch | null {
  const controls = (tile.roadControls ?? []).filter((control) =>
    control.kind !== "crossing"
  );
  if (controls.length === 0) return null;
  const placements = regulatoryRoadSignPlacementsForTile(tile, terrain);
  if (placements.length === 0) return null;
  const object = new Group();
  object.name = "runtime-regulatory-road-signs:" + tile.id;
  object.userData = {
    tileId: tile.id,
    sourceLayers: [
      "openstreetmap-road-control-nodes",
      "via-lietuva-state-road-sign-inventory",
    ],
    sourceLicenses: ["ODbL-1.0", "CC-BY-4.0"],
    placement: "offline-matched-grpk-road-directional-right-hand-roadside",
    representation:
      "lithuanian-stop-yield-no-entry-priority-speed-limit-parking-low-poly",
    renderedSignCount: placements.length,
    skippedControlCount: controls.length - placements.length,
  };
  const groups = new Map<string, RegulatoryRoadSignPlacement[]>();
  for (const placement of placements) {
    const key = placement.kind + ":" + (placement.control.value ?? "") + ":" +
      (placement.control.supplementaryCodes?.join("+") ?? "");
    const values = groups.get(key);
    if (values === undefined) groups.set(key, [placement]);
    else values.push(placement);
  }
  const matrix = new Matrix4();
  for (const [key, values] of groups) {
    const sample = values[0]!;
    for (const part of signParts(sample, materials)) {
      const mesh = new InstancedMesh(
        part.geometry,
        part.material,
        values.length,
      );
      mesh.name = object.name + ":" + key + ":" + part.name;
      mesh.castShadow = false;
      mesh.receiveShadow = false;
      for (const [index, placement] of values.entries()) {
        matrix.makeRotationY(placement.yaw);
        matrix.setPosition(placement.x, placement.y, placement.z);
        mesh.setMatrixAt(index, matrix);
      }
      mesh.instanceMatrix.needsUpdate = true;
      object.add(mesh);
    }
  }
  return {
    object,
    signCount: placements.length,
    skippedControlCount: controls.length - placements.length,
  };
}

function signParts(
  placement: Readonly<RegulatoryRoadSignPlacement>,
  materials: CityMaterialLibrary,
): SignPart[] {
  const parts: SignPart[] = [{
    name: "pole-and-brackets",
    geometry: poleGeometry(),
    material: materials.streetPole,
  }];
  parts.push(...(
    placement.kind === "stop"
      ? stopParts(materials)
      : placement.kind === "give_way"
      ? yieldParts(materials)
      : placement.kind === "no_entry"
      ? noEntryParts(materials)
      : placement.kind === "priority"
      ? priorityParts(materials)
      : placement.kind === "speed_limit"
      ? speedParts(placement.control.value!, materials)
      : parkingParts(materials)
  ));
  if ((placement.control.supplementaryCodes?.length ?? 0) > 0) {
    parts.push(...supplementaryParts(materials));
  }
  return parts;
}

function stopParts(materials: CityMaterialLibrary): SignPart[] {
  return [
    {
      name: "octagonal-back",
      geometry: discBody(0.39, 8, Math.PI / 8),
      material: materials.roadSignBack,
    },
    {
      name: "stop-red-face",
      geometry: discFace(0.382, 8, 0.033, Math.PI / 8),
      material: materials.roadSignRed,
    },
    {
      name: "stop-word",
      geometry: bitmapTextGeometry("STOP", 0.37, 0.13, 0.043),
      material: materials.roadSignIvory,
    },
  ];
}

function yieldParts(materials: CityMaterialLibrary): SignPart[] {
  return [
    {
      name: "yield-back",
      geometry: triangleFace(0.45, 0.78, 0),
      material: materials.roadSignBack,
    },
    {
      name: "yield-red-border",
      geometry: triangleFace(0.43, 0.76, 0.035),
      material: materials.roadSignRed,
    },
    {
      name: "yield-ivory-centre",
      geometry: triangleFace(0.29, 0.52, 0.044),
      material: materials.roadSignIvory,
    },
  ];
}

function noEntryParts(materials: CityMaterialLibrary): SignPart[] {
  return [
    {
      name: "no-entry-back",
      geometry: discBody(0.38, 24),
      material: materials.roadSignBack,
    },
    {
      name: "no-entry-face",
      geometry: discFace(0.372, 24, 0.033),
      material: materials.roadSignRed,
    },
    {
      name: "no-entry-bar",
      geometry: frontBox(0.47, 0.13, 0.044),
      material: materials.roadSignIvory,
    },
  ];
}

function priorityParts(materials: CityMaterialLibrary): SignPart[] {
  return [
    {
      name: "priority-back",
      geometry: diamondFace(0.48, 0),
      material: materials.roadSignBack,
    },
    {
      name: "priority-ivory-border",
      geometry: diamondFace(0.46, 0.034),
      material: materials.roadSignIvory,
    },
    {
      name: "priority-yellow-centre",
      geometry: diamondFace(0.34, 0.043),
      material: materials.roadSignYellow,
    },
  ];
}

function speedParts(
  value: number,
  materials: CityMaterialLibrary,
): SignPart[] {
  return [
    {
      name: "speed-back",
      geometry: discBody(0.38, 24),
      material: materials.roadSignBack,
    },
    {
      name: "speed-red-ring",
      geometry: discFace(0.372, 24, 0.033),
      material: materials.roadSignRed,
    },
    {
      name: "speed-ivory-centre",
      geometry: discFace(0.295, 24, 0.041),
      material: materials.roadSignIvory,
    },
    {
      name: "speed-digits",
      geometry: bitmapTextGeometry(String(Math.round(value)), 0.31, 0.23, 0.049),
      material: materials.roadSignInk,
    },
  ];
}

function parkingParts(materials: CityMaterialLibrary): SignPart[] {
  return [
    {
      name: "parking-back",
      geometry: plateBody(0.68, 0.68),
      material: materials.roadSignBack,
    },
    {
      name: "parking-blue-face",
      geometry: frontBox(0.65, 0.65, 0.033),
      material: materials.roadSignBlue,
    },
    {
      name: "parking-p",
      geometry: bitmapTextGeometry("P", 0.25, 0.4, 0.043),
      material: materials.roadSignIvory,
    },
  ];
}

function supplementaryParts(materials: CityMaterialLibrary): SignPart[] {
  const config = ROAD_SIGN_RENDER_CONFIG;
  const y = config.regulatoryPlateCenterHeightM -
    config.regulatoryPlateSizeM / 2 - config.supplementaryPlateHeightM / 2 -
    0.07;
  return [
    {
      name: "supplementary-back",
      geometry: translated(
        new BoxGeometry(
          config.supplementaryPlateWidthM,
          config.supplementaryPlateHeightM,
          0.045,
        ),
        0,
        y,
        0,
      ),
      material: materials.roadSignBack,
    },
    {
      name: "supplementary-face",
      geometry: translated(
        new BoxGeometry(
          config.supplementaryPlateWidthM - 0.03,
          config.supplementaryPlateHeightM - 0.03,
          0.012,
        ),
        0,
        y,
        0.027,
      ),
      material: materials.roadSignIvory,
    },
  ];
}

function travelDirection(
  control: Readonly<RuntimeRoadControl>,
  road: Readonly<RuntimeLinearFeature>,
): Readonly<{ x: number; z: number }> {
  const backward = control.directionAlongRoad === "backward" ||
    (
      control.directionAlongRoad === undefined &&
      road.onewayDirection === "backward"
    );
  return backward
    ? { x: -control.tangent.x, z: -control.tangent.z }
    : control.tangent;
}

function placementIsClear(
  placement: Readonly<RegulatoryRoadSignPlacement>,
  tile: Readonly<RuntimeWorldTilePayloadV2>,
): boolean {
  if (tile.buildings.some((building) =>
    pointInPolygon(placement, building.footprint)
  )) {
    return false;
  }
  return !(tile.vegetation ?? []).some((tree) =>
    Math.hypot(placement.x - tree.position.x, placement.z - tree.position.z) <
      ROAD_SIGN_RENDER_CONFIG.treeClearanceM +
        Math.max(0.08, tree.trunkDiameterM ?? 0) / 2
  );
}

function poleGeometry(): BufferGeometry {
  const config = ROAD_SIGN_RENDER_CONFIG;
  return mergeOwned([
    translated(
      new CylinderGeometry(
        config.poleRadiusM * 1.55,
        config.poleRadiusM * 1.85,
        0.11,
        8,
      ),
      0,
      0.055,
      0,
    ),
    translated(
      new CylinderGeometry(
        config.poleRadiusM,
        config.poleRadiusM * 1.08,
        config.poleHeightM,
        8,
      ),
      0,
      config.poleHeightM / 2,
      0,
    ),
    translated(new BoxGeometry(0.1, 0.06, 0.1), 0, 2.05, 0),
    translated(new BoxGeometry(0.1, 0.06, 0.1), 0, 2.5, 0),
  ], "regulatory-sign:pole");
}

function discBody(
  radius: number,
  segments: number,
  rotation = 0,
): BufferGeometry {
  const geometry = new CylinderGeometry(radius, radius, 0.055, segments);
  geometry.rotateX(Math.PI / 2);
  geometry.rotateZ(rotation);
  geometry.translate(
    0,
    ROAD_SIGN_RENDER_CONFIG.regulatoryPlateCenterHeightM,
    0,
  );
  return geometry;
}

function discFace(
  radius: number,
  segments: number,
  z: number,
  rotation = 0,
): BufferGeometry {
  const geometry = new CircleGeometry(radius, segments);
  geometry.rotateZ(rotation);
  geometry.translate(
    0,
    ROAD_SIGN_RENDER_CONFIG.regulatoryPlateCenterHeightM,
    z,
  );
  return geometry;
}

function triangleFace(
  halfWidth: number,
  height: number,
  z: number,
): BufferGeometry {
  const y = ROAD_SIGN_RENDER_CONFIG.regulatoryPlateCenterHeightM;
  const geometry = new BufferGeometry();
  geometry.setAttribute("position", new Float32BufferAttribute([
    -halfWidth, y + height / 2, z,
    halfWidth, y + height / 2, z,
    0, y - height / 2, z,
  ], 3));
  geometry.setIndex([0, 1, 2]);
  geometry.computeVertexNormals();
  return geometry;
}

function diamondFace(halfDiagonal: number, z: number): BufferGeometry {
  const y = ROAD_SIGN_RENDER_CONFIG.regulatoryPlateCenterHeightM;
  const geometry = new BufferGeometry();
  geometry.setAttribute("position", new Float32BufferAttribute([
    0, y + halfDiagonal, z,
    halfDiagonal, y, z,
    0, y - halfDiagonal, z,
    -halfDiagonal, y, z,
  ], 3));
  geometry.setIndex([0, 1, 2, 0, 2, 3]);
  geometry.computeVertexNormals();
  return geometry;
}

function plateBody(width: number, height: number): BufferGeometry {
  return translated(
    new BoxGeometry(width, height, 0.055),
    0,
    ROAD_SIGN_RENDER_CONFIG.regulatoryPlateCenterHeightM,
    0,
  );
}

function frontBox(width: number, height: number, z: number): BufferGeometry {
  return translated(
    new BoxGeometry(width, height, 0.008),
    0,
    ROAD_SIGN_RENDER_CONFIG.regulatoryPlateCenterHeightM,
    z,
  );
}

const GLYPHS: Readonly<Record<string, readonly string[]>> = Object.freeze({
  "0": ["111", "101", "101", "101", "111"],
  "1": ["010", "110", "010", "010", "111"],
  "2": ["111", "001", "111", "100", "111"],
  "3": ["111", "001", "111", "001", "111"],
  "4": ["101", "101", "111", "001", "001"],
  "5": ["111", "100", "111", "001", "111"],
  "6": ["111", "100", "111", "101", "111"],
  "7": ["111", "001", "010", "010", "010"],
  "8": ["111", "101", "111", "101", "111"],
  "9": ["111", "101", "111", "001", "111"],
  P: ["110", "101", "110", "100", "100"],
  S: ["111", "100", "111", "001", "111"],
  T: ["111", "010", "010", "010", "010"],
  O: ["111", "101", "101", "101", "111"],
});

function bitmapTextGeometry(
  text: string,
  maximumWidthM: number,
  maximumHeightM: number,
  z: number,
): BufferGeometry {
  const characters = [...text.toUpperCase()];
  const columns = Math.max(1, characters.length * 4 - 1);
  const pixel = Math.min(maximumWidthM / columns, maximumHeightM / 5);
  const width = columns * pixel;
  const height = 5 * pixel;
  const parts: BufferGeometry[] = [];
  for (const [characterIndex, character] of characters.entries()) {
    const rows = GLYPHS[character] ?? GLYPHS["0"]!;
    for (const [rowIndex, row] of rows.entries()) {
      for (let columnIndex = 0; columnIndex < row.length; columnIndex += 1) {
        if (row[columnIndex] !== "1") continue;
        parts.push(translated(
          new BoxGeometry(pixel * 0.8, pixel * 0.8, 0.008),
          -width / 2 + (characterIndex * 4 + columnIndex + 0.5) * pixel,
          ROAD_SIGN_RENDER_CONFIG.regulatoryPlateCenterHeightM + height / 2 -
            (rowIndex + 0.5) * pixel,
          z,
        ));
      }
    }
  }
  return mergeOwned(parts, "regulatory-sign:text:" + text);
}

function translated<T extends BufferGeometry>(
  geometry: T,
  x: number,
  y: number,
  z: number,
): T {
  geometry.translate(x, y, z);
  return geometry;
}

function mergeOwned(
  geometries: readonly BufferGeometry[],
  name: string,
): BufferGeometry {
  const merged = mergeGeometries([...geometries]);
  for (const geometry of geometries) geometry.dispose();
  if (merged === null) throw new Error("Could not merge " + name);
  merged.name = name;
  merged.computeBoundingBox();
  merged.computeBoundingSphere();
  return merged;
}

function sourceFeatureId(feature: Readonly<RuntimeLinearFeature>): string {
  return feature.id.split("#", 1)[0] ?? feature.id;
}
